import { type FormEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronRight, Search } from 'lucide-react'
import { createAppointment, getAppointment, updateAppointment } from '../../api/appointments'
import { listClients } from '../../api/clients'
import { getPatient, listPatients } from '../../api/patients'
import { getMyPractices, listStaff } from '../../api/staff'
import { Button } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, selectClass, textareaClass } from '../../components/ui/FormField'
import { NumberStepper } from '../../components/ui/NumberStepper'
import { useToast } from '../../components/ui/Toast'
import type { ClientListItem } from '../../types/clients'
import type { MyPractice, StaffMember } from '../../types/clinical'
import type { PatientListItem } from '../../types/patients'
import type { AppointmentStatus, AppointmentType } from '../../types/appointments'
import { STATUS_LABELS, TYPE_LABELS } from '../../types/appointments'

type FieldErrors = Partial<Record<'practiceId' | 'client' | 'patient' | 'date' | 'time' | 'duration', string>>

function todayDate(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function nowTime(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(Math.ceil(d.getMinutes() / 15) * 15 % 60).padStart(2, '0')}`
}

export default function AppointmentForm() {
  const params = useParams<{ id?: string; patientId?: string }>()
  const appointmentId = params.id ? Number(params.id) : null
  const prefilledPatientId = params.patientId ? Number(params.patientId) : null
  const isEdit = appointmentId !== null

  const navigate = useNavigate()
  const toast = useToast()

  // ── Form state ───────────────────────────────────────────────────────────────
  const [practiceId, setPracticeId] = useState('')
  const [vetId, setVetId] = useState('')
  const [date, setDate] = useState(todayDate())
  const [time, setTime] = useState(nowTime())
  const [duration, setDuration] = useState('30')
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [appointmentType, setAppointmentType] = useState<AppointmentType>('consultation')
  const [statusValue, setStatusValue] = useState<AppointmentStatus>('scheduled')

  // ── Client search (create mode only) ────────────────────────────────────────
  const [clientQuery, setClientQuery] = useState('')
  const [clientResults, setClientResults] = useState<ClientListItem[]>([])
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false)
  const [clientSearchLoading, setClientSearchLoading] = useState(false)
  const [selectedClient, setSelectedClient] = useState<ClientListItem | null>(null)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  // ── Patient (create mode) ────────────────────────────────────────────────────
  const [patients, setPatients] = useState<PatientListItem[]>([])
  const [patientsLoading, setPatientsLoading] = useState(false)
  const [selectedPatientId, setSelectedPatientId] = useState('')

  // ── Edit-mode patient display ────────────────────────────────────────────────
  const [editPatientDisplay, setEditPatientDisplay] = useState('')
  const [editClientId, setEditClientId] = useState<number | null>(null)

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [practices, setPractices] = useState<MyPractice[]>([])
  const [staff, setStaff] = useState<StaffMember[]>([])

  // ── UI ───────────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  // Load practices + existing appointment (edit mode) or prefilled patient
  useEffect(() => {
    const load = async () => {
      try {
        const [myPractices, existingAppt] = await Promise.all([
          getMyPractices(),
          isEdit && appointmentId ? getAppointment(appointmentId) : Promise.resolve(null),
        ])
        setPractices(myPractices)
        if (myPractices.length === 1) setPracticeId(String(myPractices[0].id))

        if (existingAppt) {
          setPracticeId(String(existingAppt.practice_id))
          setEditPatientDisplay(existingAppt.patient_name ?? `Patient #${existingAppt.patient_id}`)
          setEditClientId(existingAppt.client_id)
          setSelectedPatientId(String(existingAppt.patient_id))
          setVetId(existingAppt.veterinarian_id ? String(existingAppt.veterinarian_id) : '')
          const dt = new Date(existingAppt.scheduled_at)
          setDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`)
          setTime(`${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`)
          setDuration(String(existingAppt.duration_minutes))
          setReason(existingAppt.reason ?? '')
          setNotes(existingAppt.notes ?? '')
          setAppointmentType(existingAppt.appointment_type)
          setStatusValue(existingAppt.status)
        }
      } catch {
        setError('Failed to load form data.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [isEdit, appointmentId])

  // If navigating from patient detail (/patients/:patientId/appointments/new),
  // pre-load that patient's client info
  useEffect(() => {
    if (!prefilledPatientId || isEdit) return
    getPatient(prefilledPatientId).then(patient => {
      setEditPatientDisplay(patient.name)
      setSelectedPatientId(String(patient.id))
      const primary = patient.owners.find(o => o.is_primary) ?? patient.owners[0]
      if (primary) setEditClientId(primary.client_id)
    }).catch(() => {})
  }, [prefilledPatientId, isEdit])

  // Load staff when practice changes
  useEffect(() => {
    if (!practiceId) { setStaff([]); return }
    listStaff(Number(practiceId)).then(setStaff).catch(() => setStaff([]))
  }, [practiceId])

  // Debounced client search
  useEffect(() => {
    if (isEdit || prefilledPatientId) return
    const q = clientQuery.trim()
    if (!q) { setClientResults([]); setClientDropdownOpen(false); return }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setClientSearchLoading(true)
      listClients({ search: q, limit: 8 })
        .then(res => { setClientResults(res.items); setClientDropdownOpen(res.items.length > 0) })
        .catch(() => {})
        .finally(() => setClientSearchLoading(false))
    }, 280)
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [clientQuery, isEdit, prefilledPatientId])

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setClientDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  // Load patients when client selected
  useEffect(() => {
    if (!selectedClient) { setPatients([]); setSelectedPatientId(''); return }
    setPatientsLoading(true)
    listPatients({ client_id: selectedClient.id, limit: 50 })
      .then(res => {
        const active = res.items.filter(p => p.is_active)
        setPatients(active)
        if (active.length === 1) setSelectedPatientId(String(active[0].id))
      })
      .catch(() => {})
      .finally(() => setPatientsLoading(false))
  }, [selectedClient])

  const selectClient = (c: ClientListItem) => {
    setSelectedClient(c)
    setClientQuery('')
    setClientDropdownOpen(false)
    setSelectedPatientId('')
    setFieldErrors(prev => ({ ...prev, client: undefined }))
  }

  const clearClient = () => {
    setSelectedClient(null)
    setClientQuery('')
    setPatients([])
    setSelectedPatientId('')
  }

  const validate = (): boolean => {
    const errs: FieldErrors = {}
    if (!practiceId) errs.practiceId = 'Please select a practice.'
    if (!isEdit && !prefilledPatientId) {
      if (!selectedClient) errs.client = 'Please search and select a client.'
      if (!selectedPatientId) errs.patient = 'Please select a patient.'
    }
    if (!date) errs.date = 'Date is required.'
    if (!time) errs.time = 'Time is required.'
    const dur = Number(duration)
    if (!duration || isNaN(dur) || dur < 5 || dur > 480) errs.duration = 'Duration must be 5–480 minutes.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting || !validate()) return
    setSubmitting(true)
    setError(null)

    const scheduled_at = new Date(`${date}T${time}:00`).toISOString()
    const patientIdNum = Number(selectedPatientId)
    const clientIdNum = isEdit ? editClientId! : (selectedClient?.id ?? null)

    try {
      if (isEdit && appointmentId) {
        await updateAppointment(appointmentId, {
          veterinarian_id: vetId ? Number(vetId) : null,
          scheduled_at,
          duration_minutes: Number(duration),
          appointment_type: appointmentType,
          reason: reason.trim() || null,
          notes: notes.trim() || null,
          status: statusValue,
        })
        toast.success('Appointment updated.')
        void navigate(`/appointments/${appointmentId}`)
      } else {
        const created = await createAppointment({
          practice_id: Number(practiceId),
          patient_id: patientIdNum,
          client_id: clientIdNum!,
          veterinarian_id: vetId ? Number(vetId) : null,
          scheduled_at,
          duration_minutes: Number(duration),
          appointment_type: appointmentType,
          reason: reason.trim() || null,
          notes: notes.trim() || null,
        })
        toast.success('Appointment scheduled.')
        void navigate(`/appointments/${created.id}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setSubmitting(false)
    }
  }

  if (loading) return <div className="text-text-2 p-8">Loading…</div>

  const activeStaff = staff.filter(s => s.status === 'active')

  // For the prefilled-patient create flow: show read-only patient info like edit mode
  const isPatientFixed = isEdit || !!prefilledPatientId

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => void navigate(-1)}
          className="bg-transparent border-0 cursor-pointer text-accent text-sm font-medium p-0"
        >
          ← Back
        </button>
        <h1 className="text-[1.4rem] font-bold text-text-heading m-0">
          {isEdit ? 'Edit Appointment' : 'New Appointment'}
        </h1>
      </div>

      <Card>
        {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <SectionTitle>Practice</SectionTitle>

          <FormField label="Practice" required error={fieldErrors.practiceId}>
            <select
              value={practiceId}
              onChange={e => { setPracticeId(e.target.value); setFieldErrors(p => ({ ...p, practiceId: undefined })) }}
              className={selectClass(!!fieldErrors.practiceId)}
              disabled={isEdit}
            >
              <option value="">Select practice…</option>
              {practices.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
          </FormField>

          <SectionTitle className="mt-5">Client &amp; Patient</SectionTitle>

          {isPatientFixed ? (
            /* Read-only patient info for edit mode or prefilled patient */
            <FormField label="Patient">
              <div className="py-[0.6rem] px-[0.85rem] bg-surface-alt border border-border rounded text-[0.9rem] text-text">
                {editPatientDisplay || `Patient #${selectedPatientId}`}
              </div>
            </FormField>
          ) : (
            /* Client search → patient select (create mode) */
            <>
              <FormField label="Client" required error={fieldErrors.client}>
                {selectedClient ? (
                  <div className="flex items-center justify-between py-[0.6rem] px-3 border border-border-input rounded bg-surface-alt">
                    <div>
                      <span className="font-semibold text-text">
                        {selectedClient.first_name} {selectedClient.last_name}
                      </span>
                      <span className="text-text-3 text-[0.8rem] ml-2">
                        {selectedClient.email}
                      </span>
                    </div>
                    <button
                      type="button" onClick={clearClient}
                      className="bg-transparent border-0 cursor-pointer text-accent text-[0.8rem] font-semibold py-[2px] px-[6px]"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div ref={searchContainerRef} className="relative">
                    <div className="relative">
                      <Search size={14} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-text-3 pointer-events-none" />
                      <input
                        type="text"
                        value={clientQuery}
                        onChange={e => setClientQuery(e.target.value)}
                        onFocus={() => clientResults.length > 0 && setClientDropdownOpen(true)}
                        className={`${inputClass(!!fieldErrors.client)} pl-8`}
                        placeholder="Search by name, email or phone…"
                        autoComplete="off"
                        disabled={submitting}
                      />
                    </div>
                    {clientSearchLoading && (
                      <div className="text-[0.78rem] text-text-3 mt-1">Searching…</div>
                    )}
                    {clientDropdownOpen && clientResults.length > 0 && (
                      <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-[200] bg-surface border border-border rounded-lg shadow-[0_6px_24px_rgba(0,0,0,0.12)] overflow-hidden">
                        {clientResults.map((c, i) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => selectClient(c)}
                            className={`flex items-center gap-3 w-full text-left py-[0.7rem] px-[0.9rem] bg-transparent border-0 cursor-pointer text-text hover:bg-surface-alt${i > 0 ? ' border-t border-border' : ''}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-[0.875rem]">{c.first_name} {c.last_name}</div>
                              <div className="text-[0.78rem] text-text-3 overflow-hidden text-ellipsis whitespace-nowrap">
                                {c.email} · {c.patient_count} patient{c.patient_count !== 1 ? 's' : ''}
                              </div>
                            </div>
                            <ChevronRight size={14} className="text-text-3 shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </FormField>

              {selectedClient && (
                <FormField label="Patient" required error={fieldErrors.patient}>
                  {patientsLoading ? (
                    <div className="text-text-3 text-[0.875rem] py-2">Loading patients…</div>
                  ) : patients.length === 0 ? (
                    <div className="text-text-3 text-[0.875rem] py-2">No active patients for this client.</div>
                  ) : (
                    <select
                      value={selectedPatientId}
                      onChange={e => { setSelectedPatientId(e.target.value); setFieldErrors(p => ({ ...p, patient: undefined })) }}
                      className={selectClass(!!fieldErrors.patient)}
                      disabled={submitting}
                    >
                      <option value="">Select patient…</option>
                      {patients.map(p => (
                        <option key={p.id} value={String(p.id)}>
                          {p.name} — {p.species}{p.breed ? ` (${p.breed})` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                </FormField>
              )}
            </>
          )}

          <SectionTitle className="mt-5">Schedule</SectionTitle>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Date" required error={fieldErrors.date}>
              <input
                type="date" value={date}
                onChange={e => { setDate(e.target.value); setFieldErrors(p => ({ ...p, date: undefined })) }}
                className={inputClass(!!fieldErrors.date)} disabled={submitting}
              />
            </FormField>
            <FormField label="Time" required error={fieldErrors.time}>
              <input
                type="time" value={time}
                onChange={e => { setTime(e.target.value); setFieldErrors(p => ({ ...p, time: undefined })) }}
                className={inputClass(!!fieldErrors.time)} disabled={submitting}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
            <FormField label="Duration (minutes)" required error={fieldErrors.duration}>
              <NumberStepper
                value={duration}
                onChange={v => { setDuration(v); setFieldErrors(p => ({ ...p, duration: undefined })) }}
                hasError={!!fieldErrors.duration} min={5} max={480} step={5} disabled={submitting}
              />
            </FormField>
            <FormField label="Veterinarian" hint="Optional">
              <select
                value={vetId} onChange={e => setVetId(e.target.value)}
                className={selectClass()} disabled={submitting || !practiceId}
              >
                <option value="">Unassigned</option>
                {activeStaff.map(s => (
                  <option key={s.user_id} value={String(s.user_id)}>
                    {s.user_name ?? s.user_email} ({s.clinical_role.replace(/_/g, ' ')})
                  </option>
                ))}
              </select>
            </FormField>
          </div>

          <SectionTitle className="mt-5">Details</SectionTitle>

          <FormField label="Appointment type" required>
            <select
              value={appointmentType}
              onChange={e => setAppointmentType(e.target.value as AppointmentType)}
              className={selectClass()} disabled={submitting}
            >
              {(Object.keys(TYPE_LABELS) as AppointmentType[]).map(t => (
                <option key={t} value={t}>{TYPE_LABELS[t]}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Reason for visit" hint="Optional">
            <input
              type="text" value={reason} onChange={e => setReason(e.target.value)}
              className={inputClass()} placeholder="e.g. Annual check-up, limping on right foreleg…"
              maxLength={500} disabled={submitting}
            />
          </FormField>

          <FormField label="Notes" hint="Optional">
            <textarea
              value={notes} onChange={e => setNotes(e.target.value)}
              className={textareaClass()}
              placeholder="Any additional notes…" disabled={submitting}
            />
          </FormField>

          {isEdit && (
            <FormField label="Status">
              <select value={statusValue} onChange={e => setStatusValue(e.target.value as AppointmentStatus)} className={selectClass()} disabled={submitting}>
                {(Object.keys(STATUS_LABELS) as AppointmentStatus[]).map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </FormField>
          )}

          <div className="flex gap-3 mt-7 pt-6 border-t border-border">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Schedule Appointment'}
            </Button>
            <Button type="button" variant="outline" onClick={() => void navigate(-1)} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
